/**
 * MemoryReviewQueueCard — Displays memories needing human review
 *
 * Shows:
 * - Count of items needing review
 * - Grouped by review reason
 * - Sample items for quick inspection
 */

import React, { useState } from 'react';
import {
  ClipboardList,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Loader2,
  AlertTriangle,
  AlertCircle,
  Eye,
  User,
  FileText,
  ExternalLink
} from 'lucide-react';

function MemoryReviewQueueCard({ data, isLoading, error, onRefresh }) {
  const [expanded, setExpanded] = useState(true);
  const [showingSamples, setShowingSamples] = useState(false);

  if (isLoading) {
    return (
      <div className="md-card md-card--loading">
        <div className="md-card-header">
          <ClipboardList size={18} />
          <span>Review Queue</span>
        </div>
        <div className="md-card-loading">
          <Loader2 size={24} className="md-spin" />
          <span>Loading review queue...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="md-card md-card--error">
        <div className="md-card-header">
          <ClipboardList size={18} />
          <span>Review Queue</span>
          <button className="md-card-refresh" onClick={onRefresh}>
            <RefreshCw size={14} />
          </button>
        </div>
        <div className="md-card-error">
          <AlertTriangle size={20} />
          <span>Failed to load review queue</span>
          <code>{error.message}</code>
        </div>
      </div>
    );
  }

  const total = data?.total || 0;
  const byReason = data?.by_reason || {};
  const samples = data?.samples || [];

  const getStatusClass = () => {
    if (total === 0) return 'md-card--pass';
    if (total <= 50) return 'md-card--warn';
    return 'md-card--fail';
  };

  const getReasonIcon = (reason) => {
    switch (reason) {
      case 'low_confidence':
        return <AlertCircle size={14} />;
      case 'low_health':
        return <AlertTriangle size={14} />;
      case 'negative_feedback':
        return <AlertTriangle size={14} />;
      case 'conflict':
        return <AlertCircle size={14} />;
      default:
        return <Eye size={14} />;
    }
  };

  const formatReason = (reason) => {
    return reason
      .replace(/_/g, ' ')
      .replace(/^./, (s) => s.toUpperCase());
  };

  return (
    <div className={`md-card ${getStatusClass()}`}>
      <div
        className="md-card-header md-card-header--clickable"
        onClick={() => setExpanded(!expanded)}
      >
        <ClipboardList size={18} />
        <span>Review Queue</span>
        <div className="md-card-header-right">
          <span className={`md-count-badge ${total > 50 ? 'md-count-badge--alert' : total > 0 ? 'md-count-badge--warn' : 'md-count-badge--ok'}`}>
            {total}
          </span>
          <button className="md-card-refresh" onClick={(e) => { e.stopPropagation(); onRefresh(); }}>
            <RefreshCw size={14} />
          </button>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {expanded && (
        <div className="md-card-content">
          {total === 0 ? (
            <div className="md-all-clear">
              <ClipboardList size={20} />
              <span>No items need review</span>
            </div>
          ) : (
            <>
              {/* By Reason Breakdown */}
              <div className="md-reason-list">
                <h5 className="md-reason-title">By Reason</h5>
                {Object.entries(byReason)
                  .sort((a, b) => b[1] - a[1])
                  .map(([reason, count]) => (
                    <div key={reason} className="md-reason-item">
                      {getReasonIcon(reason)}
                      <span className="md-reason-name">{formatReason(reason)}</span>
                      <span className="md-reason-count">{count}</span>
                    </div>
                  ))}
              </div>

              {/* Sample Items */}
              {samples.length > 0 && (
                <div className="md-samples">
                  <button
                    className="md-samples-toggle"
                    onClick={() => setShowingSamples(!showingSamples)}
                  >
                    <Eye size={14} />
                    {showingSamples ? 'Hide Samples' : `View ${samples.length} Sample${samples.length !== 1 ? 's' : ''}`}
                    {showingSamples ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>

                  {showingSamples && (
                    <div className="md-samples-list">
                      {samples.map((sample, idx) => (
                        <div key={sample.id || idx} className="md-sample-item">
                          <div className="md-sample-header">
                            <span className="md-sample-type">{sample.fact_type || 'fact'}</span>
                            <span className="md-sample-reason">{formatReason(sample.review_reason || 'unknown')}</span>
                          </div>
                          <div className="md-sample-content">
                            <FileText size={12} />
                            <span>{truncate(sample.fact || sample.content, 100)}</span>
                          </div>
                          <div className="md-sample-meta">
                            <span className="md-sample-user">
                              <User size={10} />
                              {sample.user_id?.slice(0, 8) || 'Unknown'}
                            </span>
                            <span className="md-sample-confidence">
                              Confidence: {formatPercent(sample.confidence)}
                            </span>
                            <span className="md-sample-health">
                              Health: {formatPercent(sample.health_score)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Action Hint */}
              <div className="md-review-hint">
                <AlertCircle size={14} />
                <span>
                  Review items via <code>GET /api/memory/digest/review-queue</code> or the Memory Digest endpoint.
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function truncate(str, maxLen) {
  if (!str) return '-';
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen - 3) + '...';
}

function formatPercent(value) {
  if (value === undefined || value === null) return '-';
  return `${(value * 100).toFixed(0)}%`;
}

export default MemoryReviewQueueCard;
